// src/components/Links.js
import { useState, useEffect } from "react";
import { Apple, Smartphone, Instagram, Send, MessageCircle } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

const LINKS = {
  customerIos: "https://apps.apple.com/sa/app/trustfix/id6809151329",
  taskerIos: "https://apps.apple.com/sa/app/trustfix-partners/id6797325119",
  taskerAndroid:
    "https://play.google.com/store/apps/details?id=com.trustfix.partnerapp",
  instagram: "https://www.instagram.com/trustfixs/",
  telegram: "https://t.me/+KECChCjVYgAwZGFk",
  whatsapp: "https://whatsapp.com/channel/0029VbDvIdc7YSdCfkPDI633",
};

const CONTENT = {
  ar: {
    title: "روابط ترستفيكس",
    subtitle: "تطبيق ترستفيكس — TrustFix",
    tagline: "منصّة الصيانة والخدمات المنزلية — الإطلاق 22 أكتوبر",
    customersTitle: "للعملاء 🏠",
    taskersTitle: "للفنيين 🔧",
    eduTitle: "قنوات الفنيين التعليمية 🎥",
    eduCaption: "فيديوهات شرح كيفية عمل التطبيق وآخر الإعلانات لمقدّمي الخدمة.",
    followTitle: "تابعنا",
    items: {
      customerIos: "اطلب فنّي — آيفون",
      taskerIos: "انضم كفنّي — آيفون",
      taskerAndroid: "انضم كفنّي — أندرويد",
      instagram: "إنستغرام",
      telegram: "قناة تيليجرام",
      whatsapp: "قناة واتساب",
    },
    note: "نسخة الأندرويد للعملاء قريباً 🚀",
  },
  en: {
    title: "TrustFix Links",
    subtitle: "TrustFix — ترستفيكس",
    tagline: "Home maintenance & services — launching October 22",
    customersTitle: "For Customers 🏠",
    taskersTitle: "For Taskers 🔧",
    eduTitle: "Provider Learning Channels 🎥",
    eduCaption:
      "How-to videos on using the app and the latest announcements, for service providers.",
    followTitle: "Follow Us",
    items: {
      customerIos: "Request a technician — iPhone",
      taskerIos: "Join as a technician — iPhone",
      taskerAndroid: "Join as a technician — Android",
      instagram: "Instagram",
      telegram: "Telegram channel",
      whatsapp: "WhatsApp channel",
    },
    note: "Android version for customers coming soon 🚀",
  },
};

function LinkButton({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 w-full bg-white border border-gray-200 rounded-xl px-5 py-4 shadow-sm hover:border-[#006C35] hover:shadow transition-all"
    >
      <span className="w-9 h-9 shrink-0 bg-[#EDEEF2] rounded-lg flex items-center justify-center text-[#006C35]">
        {icon}
      </span>
      <span className="font-semibold text-gray-800">{label}</span>
    </a>
  );
}

export default function Links() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("tf_lang") || "ar";
    } catch {
      return "ar";
    }
  });

  const isAr = lang === "ar";
  const t = CONTENT[lang];
  const L = t.items;

  useEffect(() => {
    try {
      localStorage.setItem("tf_lang", lang);
    } catch {}
  }, [lang]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header lang={lang} setLang={setLang} />

      <div dir={isAr ? "rtl" : "ltr"}>
        {/* Title */}
        <section>
          <div className="max-w-md mx-auto px-5 pt-6 pb-2 text-center">
            <h1 className="text-[26px] font-extrabold text-[#006C35] mt-2 mb-1">
              {t.title}
            </h1>
            <p className="text-gray-500 text-[15px] mt-0 mb-2">{t.subtitle}</p>
            <p className="text-gray-600">{t.tagline}</p>
          </div>
        </section>

        {/* Links */}
        <section className="bg-white">
          <div className="max-w-md mx-auto px-5 pt-8 pb-12">
            {/* Customers */}
            <h2 className="text-[#006C35] font-bold mb-3">{t.customersTitle}</h2>
            <div className="space-y-3 mb-3">
              <LinkButton
                href={LINKS.customerIos}
                icon={<Apple size={18} />}
                label={L.customerIos}
              />
            </div>
            <p className="text-sm text-gray-500 mb-8">{t.note}</p>

            {/* Taskers */}
            <h2 className="text-[#006C35] font-bold mb-3">{t.taskersTitle}</h2>
            <div className="space-y-3 mb-8">
              <LinkButton
                href={LINKS.taskerIos}
                icon={<Apple size={18} />}
                label={L.taskerIos}
              />
              <LinkButton
                href={LINKS.taskerAndroid}
                icon={<Smartphone size={18} />}
                label={L.taskerAndroid}
              />
            </div>

            {/* Provider learning channels */}
            <h2 className="text-[#006C35] font-bold mb-1">{t.eduTitle}</h2>
            <p className="text-sm text-gray-500 mb-3">{t.eduCaption}</p>
            <div className="space-y-3 mb-8">
              <LinkButton
                href={LINKS.telegram}
                icon={<Send size={18} />}
                label={L.telegram}
              />
              <LinkButton
                href={LINKS.whatsapp}
                icon={<MessageCircle size={18} />}
                label={L.whatsapp}
              />
            </div>

            {/* Follow */}
            <h2 className="text-[#006C35] font-bold mb-3">{t.followTitle}</h2>
            <div className="space-y-3">
              <LinkButton
                href={LINKS.instagram}
                icon={<Instagram size={18} />}
                label={L.instagram}
              />
            </div>
          </div>
        </section>
      </div>

      <Footer lang={lang} />
    </div>
  );
}